/**
 * Purpose: Client wrapper for POST /api/face-analysis (survey selfie).
 * Inputs: Raw JPEG base64 string, plus the optional research-use opt-in.
 * Outputs: FaceAnalysisResponse; throws mapped errors for 503/422/429.
 */

import type { FaceAnalysisResponse } from '@lib/models/face-api'

/** Thrown when the face-analysis route returns 429 with an optional retry timestamp. */
export class RateLimitedError extends Error {
  constructor(public retryAfter: string) {
    super('rate_limited')
  }
}

/**
 * `researchConsent` is the user's separate, optional opt-in to research use. It
 * only lengthens how long the image is kept — the scan runs identically either
 * way — so it defaults to false rather than being required.
 */
export async function callFaceAnalysis(
  base64: string,
  researchConsent = false,
): Promise<FaceAnalysisResponse> {
  const res = await fetch('/api/face-analysis', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64, researchConsent }),
  })
  // 503 covers not-configured and vision-unavailable — read the body to split them.
  if (res.status === 503) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    if (body.error === 'vision_unavailable') throw new Error('vision_unavailable')
    throw new Error('not_configured')
  }
  // 422 covers multiple faces, blur, underexposure, and unsafe content — read the
  // actual code from the body instead of assuming multiple_faces for all of them.
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? 'multiple_faces')
  }
  // Surface retryAfter so the survey UI can show when the quota resets.
  if (res.status === 429) {
    const body = (await res.json().catch(() => ({}))) as { retryAfter?: string }
    throw new RateLimitedError(body.retryAfter ?? '')
  }
  if (!res.ok) throw new Error(`face_analysis_${res.status}`)
  return res.json()
}
